<template>
  <div class="flex w-full flex-col">
    <div class="tabs absolute flex w-full pr-12">
      <lf-scrollable-menu
        :scroll-length="150"
        :styling-options="{
          buttonsTransparent: true,
          offsetRightClasses: 'top-10 -right-12',
          offsetLeftClasses: 'top-10 -left-12',
        }"
      >
        <div class="flex space-x-2">
          <div
            v-for="tab in tabs"
            :key="tab.id"
            :class="
              activeTab === tab.id
                ? 'border-gray-20 z-2 mb-3 h-[143px] rounded-t border border-b-0 bg-gray-100'
                : 'h-33 bg-white px-0.5 pt-3'
            "
            class="w-46 cursor-pointer text-sm font-medium text-gray-600 focus:outline-hidden"
            data-cy="folder-tab"
            @click="changeTab(tab.id ?? '')"
          >
            <div
              class="h-full space-y-2 rounded-md p-3"
              :class="{
                'border-t border-gray-100 shadow-sm': activeTab !== tab.id,
              }"
            >
              <div class="flex items-center">
                <div class="truncate" data-cy="tab-title">
                  {{ tab.is_published ? tab.name : $t("PRESETS.NO_PRESET") }}
                </div>
                <div v-if="tab.id === assignedPreset">
                  <icon-base
                    :icon="IconSuccessRoundBig"
                    class="ml-1 text-green-500"
                  />
                </div>
              </div>
              <div
                class="truncate text-xs font-light whitespace-pre-wrap text-gray-500"
                data-cy="tab-description"
              >
                {{ truncateWithEllipsis(tab.description || "", 80) }}
              </div>
            </div>
          </div>
        </div>
      </lf-scrollable-menu>
    </div>
    <div class="tab-content z-1 mt-36 p-5">
      <slot></slot>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref } from "vue";

import { truncateWithEllipsis } from "@/helpers/formatting";

import IconSuccessRoundBig from "@/components/icons/IconSuccessRoundBig.vue";

import type { PermissionsPreset } from "@/models/permissionsPreset";
import type { PropType } from "vue";

const props = defineProps({
  tabs: {
    type: Array as PropType<Partial<PermissionsPreset>[]>,
    required: true,
  },
  activeTabId: {
    type: String,
    default: "",
  },
  assignedPreset: {
    type: String,
    default: "",
  },
});

const emit = defineEmits<{
  (eventName: "change", tabId: string): void;
}>();

const activeTab = ref<string>(props.activeTabId || props.tabs[0]?.id || "");

const changeTab = (tabId: string) => {
  activeTab.value = tabId ?? "";
  emit("change", tabId);
};
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.tabs > div {
  @apply cursor-pointer rounded-t-md text-sm font-medium focus:ring focus:outline-hidden;
}
.tabs > div:focus {
  @apply bg-gray-100 text-gray-900;
}

.tabs > div.active {
  @apply bg-gray-100 text-gray-900;
}

.tab-content {
  @apply rounded-b-md border border-gray-200 bg-gray-100 p-4;
}
</style>
